import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, Loader2, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";

const DEFAULT_SUGGESTIONS = [
  "What's overdue?",
  "What's waiting in review?",
  "What's due today?",
  "What did we complete this week?",
  "Who has the most open tasks?",
];

// Minimal, safe markdown: paragraphs, "- " / "• " bullets, **bold**, #12 refs and bare URLs.
function renderInline(text) {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|https?:\/\/\S+|#\d+)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      parts.push(<strong key={i++} className="font-semibold text-slate-900">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("http")) {
      const clean = tok.replace(/[).,]+$/, "");
      const focus = clean.match(/focus=([0-9a-f-]+)/i);
      parts.push(
        focus
          ? <Link key={i++} to={`/app/tasks?focus=${focus[1]}`} className="text-blue-600 underline">open task</Link>
          : <a key={i++} href={clean} target="_blank" rel="noreferrer" className="text-blue-600 underline">{clean}</a>
      );
      if (tok.length !== clean.length) parts.push(tok.slice(clean.length));
    } else {
      parts.push(<span key={i++} className="font-mono text-[12px] bg-slate-100 rounded px-1 text-slate-700">{tok}</span>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function Answer({ text }) {
  const lines = (text || "").split("\n");
  const blocks = [];
  let list = null;
  lines.forEach((raw, idx) => {
    const line = raw.replace(/^#{1,3}\s+/, "");
    const bullet = line.match(/^\s*(?:[-*•]|\d+[.)])\s+(.*)$/);
    if (bullet) {
      if (!list) { list = []; blocks.push(list); }
      list.push(bullet[1]);
    } else {
      list = null;
      if (line.trim()) blocks.push(line);
    }
  });
  return (
    <div className="space-y-2 text-[13.5px] text-slate-700 leading-relaxed">
      {blocks.map((b, i) =>
        Array.isArray(b)
          ? <ul key={i} className="space-y-1 pl-4 list-disc marker:text-slate-400">{b.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>
          : <p key={i}>{renderInline(b)}</p>
      )}
    </div>
  );
}

export default function AskBrainydo() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);          // {id, question, answer, asked_by, created_at, pending}
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(DEFAULT_SUGGESTIONS);
  const [open, setOpen] = useState(true);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [h, s] = await Promise.all([api.get("/assistant/history?limit=20"), api.get("/assistant/suggestions")]);
        setItems(h.data.items || []);
        if (s.data.suggestions?.length) setSuggestions(s.data.suggestions);
      } catch {
        // panel still works without history
      }
    })();
  }, []);

  useEffect(() => {
    if (items.length) endRef.current?.scrollIntoView({ block: "nearest" });
  }, [items]);

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (!text || busy) return;
    setQuestion("");
    setBusy(true);
    const tempId = `tmp-${Date.now()}`;
    setItems((cur) => [...cur, { id: tempId, question: text, answer: null, pending: true }]);
    try {
      const history = items.slice(-6).flatMap((it) => [
        { role: "user", content: it.question },
        { role: "assistant", content: it.answer || "" },
      ]);
      const res = await api.post("/assistant/ask", { question: text, history });
      setItems((cur) => cur.map((it) => it.id === tempId
        ? { id: res.data.id || tempId, question: text, answer: res.data.answer, created_at: new Date().toISOString() }
        : it));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't get an answer right now.";
      setItems((cur) => cur.map((it) => it.id === tempId ? { ...it, answer: msg, pending: false, error: true } : it));
      if (e?.response?.status === 429) toast.error(msg);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const clear = async () => {
    const ok = await confirm({
      title: "Clear this conversation?",
      message: "Your questions and answers will be removed from the dashboard.",
      confirmLabel: "Clear",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete("/assistant/history");
      setItems([]);
    } catch {
      toast.error("Couldn't clear the conversation");
    }
  };

  return (
    <section className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="ask-brainydo">
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-violet-50 border border-violet-200 grid place-items-center">
            <Sparkles size={16} className="text-violet-600" />
          </div>
          <div>
            <div className="text-[14px] font-bold text-slate-900 leading-tight">Ask Brainydo</div>
            <div className="text-[12px] text-slate-500">Ask about your tasks, review queue, completed work or any chat — or tell it what to do: complete, approve, assign, reschedule, create.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <button onClick={clear} className="h-8 w-8 grid place-items-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 transition" title="Clear conversation" data-testid="ask-brainydo-clear">
              <Trash2 size={14} />
            </button>
          )}
          <button onClick={() => setOpen((o) => !o)} className="h-8 px-3 rounded-lg border border-slate-200 text-[12px] font-semibold text-slate-600 hover:border-slate-400 transition" data-testid="ask-brainydo-toggle">
            {open ? "Hide" : "Show"}
          </button>
        </div>
      </div>

      {open && (
        <div className="px-5 py-4">
          {items.length === 0 ? (
            <div className="text-[13px] text-slate-500 mb-3">
              Try one of these, or type your own question below. You can also ask the same things by messaging the Brainydo WhatsApp number once your number is verified in Settings.
            </div>
          ) : (
            <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1 mb-4" data-testid="ask-brainydo-thread">
              {items.map((it) => (
                <div key={it.id} className="space-y-2">
                  <div className="flex justify-end">
                    <div className="max-w-[85%] bg-blue-600 text-white text-[13.5px] rounded-2xl rounded-br-md px-4 py-2.5 leading-relaxed">
                      {it.question}
                      {it.asked_by && <div className="text-[10.5px] text-blue-100 mt-1">{it.asked_by}</div>}
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-violet-50 border border-violet-200 grid place-items-center shrink-0 mt-0.5">
                      <Sparkles size={13} className="text-violet-600" />
                    </div>
                    <div className={"max-w-[85%] rounded-2xl rounded-tl-md px-4 py-3 border " + (it.error ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200")}>
                      {it.pending
                        ? <div className="flex items-center gap-2 text-[13px] text-slate-500"><Loader2 size={14} className="animate-spin" /> Looking that up…</div>
                        : <Answer text={it.answer} />}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-3">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => ask(s)}
                disabled={busy}
                className="h-8 px-3 rounded-full border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:border-violet-400 hover:text-violet-700 disabled:opacity-50 transition"
                data-testid="ask-brainydo-suggestion"
              >
                {s}
              </button>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="flex items-center gap-2"
          >
            <div className="relative flex-1">
              <MessageCircle size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask anything, e.g. what's on Priya's plate this week?"
                disabled={busy}
                maxLength={2000}
                className="w-full h-11 pl-9 pr-3 border border-slate-200 rounded-xl text-[13.5px] bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition disabled:opacity-60"
                data-testid="ask-brainydo-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !question.trim()}
              className="h-11 px-4 inline-flex items-center gap-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-semibold disabled:opacity-50 transition active:scale-95"
              data-testid="ask-brainydo-send"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Ask
            </button>
          </form>
          <div className="text-[11px] text-slate-400 mt-2">It can act too: "mark #12 done", "assign the Ramesh task to Priya", "move #7 to tomorrow 3 pm", "approve everything from Suresh", "add a task: send invoice to Regenta by Friday".</div>
        </div>
      )}
    </section>
  );
}
